import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Stack,
  TextField,
  Button,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { GetApp as GetAppIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DynamicTable from 'src/components/shared/DynamicTable';
import CustomFilter from 'src/components/filter/CustomFilter';
import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import moment from 'moment';
import api from 'src/services/BaseService';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import logo from 'src/assets/images/logo-form.png';
import useResponsive from 'src/hooks/useResponsive';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default function InvoicePaymentList() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const { isDesktop } = useResponsive(); // isDesktop kullanımı opsiyonel
  const [filterModal, setFilterModal] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);

  // Tablo sütun tanımı
  const INVOICE_TABLE_HEAD = [
    { id: 'invoiceNo', label: 'Fatura No', format: (_, r) => r.invoiceNo },
    { id: 'vkn', label: 'VKN ve TCKN', format: (_, r) => r.vkn },
    { id: 'title', label: 'Ticari Ünvan', format: (_, r) => r.title },
    { id: 'amount', label: 'Ödeme Tutarı', format: (_, r) => r.amount },
    { id: 'date', label: 'Ödeme Tarihi', format: (_, r) => r.date },
    { id: 'paymentNo', label: 'Ödeme No', format: (_, r) => r.paymentNo },
    { id: 'phone', label: 'Tel No', format: (_, r) => r.phone },
    {
      id: 'receipt',
      label: 'Ödeme Belgesi',
      format: (_, r) => (
        <GetAppIcon
          style={{ cursor: 'pointer' }}
          onClick={() => generatePdf(r)}
        />
      ),
    },
  ];

  // Yardımcı fonksiyonlar
  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  const truncate2 = (num) => Math.floor((num || 0) * 100) / 100;

  // Tek satır PDF oluşturma
  const generatePdf = (item) => {
    const pageWidthPt = 595.28;
    const marginPt = 40 * 2;
    const contentWidth = pageWidthPt - marginPt;
    const PADDING = 8;
    const BOTTOM_SPACE = 30;
    const currency = item.raw.currency || 'TRY';

    const totalService =
      item.raw.lineItems?.reduce((sum, li) => sum + truncate2(li.tutar), 0) ||
      0;
    const totalWithVat = truncate2(item.raw.price || 0);

    const invoiceData = {
      invoiceNumber: item.raw.invoiceID || '',
      invoiceNo: item.raw.invoiceID || '',
      taxNo: item.raw.taxNo || '',
      paymentID: item.paymentNo || '',
      invoiceDate: item.raw.invoiceDate ? formatDate(item.raw.invoiceDate) : '',
      paymentDate: item.date,
      fromName: item.raw.from.companyTitle || '',
      explanationTitle: item.raw.description || '',
      explanationDetail: item.raw.description || '',
      subtotal: totalService.toLocaleString('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      total: totalWithVat.toLocaleString('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      conversationID: item.paymentNo || '-',
    };

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      content: [
        // Logo ve Başlık
        {
          columns: [
            { image: logo, width: 80 },
            {
              width: '*',
              stack: [
                {
                  text: 'AVM Lojistik A.Ş.',
                  fontSize: 12,
                  alignment: 'center',
                },
                {
                  text: 'ÖDEME ALINDISI',
                  fontSize: 18,
                  bold: true,
                  color: '#006366',
                  alignment: 'center',
                  margin: [0, 4, 0, 0],
                },
              ],
            },
          ],
          columnGap: 10,
          margin: [0, 0, 0, 15],
        },
        // Özet Kutusu 1
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 98,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['auto', 'auto', '*'],
                body: [
                  [
                    { text: 'ÖDEME NUMARASI', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.paymentID, fontSize: 10 },
                  ],
                  [
                    { text: 'FİRMA ÜNVANI', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.fromName, fontSize: 10 },
                  ],
                  [
                    { text: 'VERGİ DAİRESİ VE NO', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.taxNo, fontSize: 10 },
                  ],
                  [
                    { text: 'ÖDEME TARİHİ', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.paymentDate, fontSize: 10 },
                  ],
                  [
                    {
                      text: 'GENEL TOPLAM (KARTTAN ÖDENEN)',
                      bold: true,
                      fontSize: 10,
                    },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.total, fontSize: 10 },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(98 - PADDING), PADDING, BOTTOM_SPACE],
            },
          ],
        },
        // Özet Kutusu 2
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 80,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['*', '*'],
                body: [
                  [
                    { text: 'FATURA NUMARASI :', bold: true, fontSize: 10 },
                    {
                      text: 'TUTARI',
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                  [
                    { text: invoiceData.invoiceNo, fontSize: 10 },
                    {
                      text: invoiceData.subtotal,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                  [
                    {
                      text: '',
                      italics: true,
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    {},
                  ],
                  [
                    { text: 'TOPLAM TUTAR :', bold: true, fontSize: 10 },
                    {
                      text: invoiceData.total,
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(80 - PADDING), PADDING, BOTTOM_SPACE],
            },
          ],
        },
        // Özet Kutusu 3
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 60,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['*', '*', '*'],
                body: [
                  [
                    { text: 'Tahsilat Şekli :', bold: true, fontSize: 10 },
                    { text: 'İşlem Türü :', bold: true, fontSize: 10 },
                    { text: 'Ödeme Şekli :', bold: true, fontSize: 10 },
                  ],
                  [
                    { text: invoiceData.explanationTitle, fontSize: 10 },
                    { text: invoiceData.explanationDetail, fontSize: 10 },
                    { text: 'KREDİ KARTI', fontSize: 10 },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(60 - PADDING), PADDING, 0],
            },
          ],
        },
      ],
    };

    pdfMake
      .createPdf(docDefinition)
      .download(`Odeme_Alindi_${moment().format('DDMMYYYY_HHmm')}.pdf`);
  };

  // Veri çekme
  const fetchInvoices = () => {
    api
      .post('flow/get-e-payments', {
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      })
      .then((res) => {
        if (res.data.status) {
          const uniqueInvoices = Array.from(
            res.data.result
              .filter((inv) => inv.status === '1' && inv.isPaid === '1')
              .reduce((map, inv) => {
                if (!map.has(inv.invoiceID)) {
                  map.set(inv.invoiceID, inv);
                }
                return map;
              }, new Map())
              .values()
          );

          setData(
            uniqueInvoices.map((inv) => ({
              invoiceNo: inv.invoiceID,
              vkn: inv.taxNo,
              title: inv.dataJSON.to.companyTitle || '',
              amount: `${inv.price}₺`,
              date: inv.paymentCompleteDateTime
                ? moment
                    .unix(parseInt(inv.paymentCompleteDateTime, 10))
                    .format('DD.MM.YYYY HH:mm')
                : '',
              paymentNo: inv.paymentID,
              phone: inv.dataJSON.to.phone || '',
              raw: inv.dataJSON,
            }))
          );
        } else {
          setData([]);
        }
      })
      .catch(() => setData([]));
  };

  useEffect(() => {
    fetchInvoices();
  }, [startDate, endDate]);

  // Case-insensitive filtreleme ve arama
  const filteredData = data.filter((row) => {
    const byDate =
      startDate && endDate
        ? moment(row.date, 'DD.MM.YYYY HH:mm').isBetween(
            startDate.startOf('day'),
            endDate.endOf('day'),
            undefined,
            '[]'
          )
        : true;
    const bySearch = row.invoiceNo
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return byDate && bySearch;
  });

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  // Excel'e aktarma
  const exportToExcel = () => {
    const temp = filteredData.map((row, idx) => ({
      No: idx + 1,
      'Fatura No': row.invoiceNo,
      'VKN ve TCKN': row.vkn,
      'Ticari Ünvan': row.title,
      'Ödeme Tutarı': row.amount,
      'Ödeme Tarihi': row.date,
      'Ödeme No': row.paymentNo,
      'Tel No': row.phone,
    }));

    api
      .post('excel/export', {
        type: 'excel',
        content: temp,
        header: {
          No: 'number',
          'Fatura No': 'string',
          'VKN ve TCKN': 'string',
          'Ticari Ünvan': 'string',
          'Ödeme Tutarı': 'string',
          'Ödeme Tarihi': 'string',
          'Ödeme No': 'string',
          'Tel No': 'string',
        },
        sheetTitle: 'Fatura Ödemeleri',
      })
      .then((response) => {
        if (response.data.status) {
          const newWindow = window.open(response.data.result, '_blank');
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed === 'undefined'
          ) {
            throw new Error(
              'Pop-up engellendi. Lütfen pop-up engelleyicisini devre dışı bırakın.'
            );
          }
        }
      })
      .catch((err) => console.error(err));
  };

  return (
    <>
      <Helmet>
        <title>Fatura Ödeme Listesi | Mall Logistics Panel</title>
      </Helmet>

      <Stack spacing={4} sx={{ p: 3 }}>
        {/* Filtre ve Excel butonları */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <FilterButton onClick={() => setFilterModal(true)}>
            Filtrele
          </FilterButton>
          <Button
            variant="contained"
            color="success"
            startIcon={<GetAppIcon />}
            onClick={exportToExcel}
          >
            Excele Aktar
          </Button>
        </Stack>

        <Card>
          {/* Filtre Drawer */}
          <CustomFilter
            modalType="drawer"
            filterModal={filterModal}
            handleCloseModal={() => setFilterModal(false)}
          >
            <Stack spacing={2} sx={{ p: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Başlangıç Tarihi"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />
                <DatePicker
                  label="Bitiş Tarihi"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />
              </LocalizationProvider>

              <Divider />

              <Stack direction="row" spacing={1} justifyContent="center">
                <ClearButton onClick={clearFilters} />
                <FilterButton onClick={() => setFilterModal(false)}>
                  Uygula
                </FilterButton>
              </Stack>
            </Stack>
          </CustomFilter>

          {/* Tablo + Hızlı Arama */}
          <DynamicTable
            data={filteredData}
            headers={INVOICE_TABLE_HEAD}
            isMobile={isMobile}
            isOrdered
          >
            <TextField
              variant="outlined"
              label="Hızlı Arama"
              placeholder="Fatura No"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2, width: isMobile ? '100%' : 300 }}
            />
          </DynamicTable>
        </Card>
      </Stack>
    </>
  );
}
